import React, { useState, useRef } from 'react';
import { Datagrid, TextField, FunctionField, useNotify, EmailField } from 'react-admin';
import { Chip, IconButton } from '@mui/material';
import RaList from '../../../../components/ra-list/RaList';
import RenderControls from '../../../../components/ra-list/RenderControls';
import { formatDateTime, toLocalTime } from '../../../../components/ex_dayjs';
import SeatActionDialog, { PURCHASE_STATUS_OPTIONS } from '../../components/SeatActionDialog';
import { AIPurchasedTicket, FilterValues } from '../../models/seat';
import { getScoreColor } from '../../AnalysisPage';
import { getActionChipColor } from '../../components/tabs/shared/SeatRecommendationComponent';
import { dataProvider } from '../../../../DataProvider';
import { WarningAmber, Visibility } from '@mui/icons-material';
import { EventItem } from '../../models/event';
import { TrackingVersion } from '..';

interface PurchaseSeatsTabProps {
    marketType?: any[];
    filterValues: FilterValues;
    setFilterValues: (values: FilterValues) => void;
    setActiveTab: (tabIndex: number) => void;
    setSelectedEvent: (target: EventItem | null) => void;
}

/**
 * PurchaseSeatsTab Component
 *
 * Displays a filterable table of purchased tickets from AI_PurchasedTickets table.
 * Allows users to view and update purchase information with filters similar to
 * ProblemOrderTrackingReport.
 *
 * Features:
 * - OData-based filtering and pagination
 * - Row click to open purchase detail dialog
 * - Filters for section, row, seat range, status, date, and user
 * - Export functionality
 */
const PurchaseSeatsTab: React.FC<PurchaseSeatsTabProps> = ({ marketType, filterValues, setFilterValues, setActiveTab, setSelectedEvent }) => {
    const refbtnFilter = useRef<HTMLButtonElement>(null);
    const notify = useNotify();
    const [defaultSort] = useState(['lastSaleDate desc']);
    const [seatActionDialogOpen, setSeatActionDialogOpen] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState<AIPurchasedTicket | null>(null);
    const [defaultFilter] = useState<FilterValues>(filterValues || {});

    const handleFilterChange = (params: { name: string; value: string | number }) => {
        setFilterValues((prev: FilterValues) => ({ ...prev, [params.name]: params.value }));
    };
    // Handle change in date range filters
    const handleDateRangeChange = (params: { name: string; value: string | number }) => {
        // Remove _ge, _le suffix to match the filter field names
        let finalName = params.name.replace(/(_ge|_le)$/, '');
        // finalName receives value is array with two elements [from: string, to: string]
        // Need to check _ge or _le to set the correct value, if _ge not present, set to null, if _le not present, set to null
        if (params.name.endsWith('_ge')) {
            const toValue = filterValues[finalName]?.[1] || null;
            setFilterValues((prev: FilterValues) => ({ ...prev, [finalName]: [params.value, toValue] }));
            return;
        } else if (params.name.endsWith('_le')) {
            const fromValue = filterValues[finalName]?.[0] || null;
            setFilterValues((prev: FilterValues) => ({ ...prev, [finalName]: [fromValue, params.value] }));
            return;
        }
    };

    /**
     * Filter controls configuration
     * Matches the columns specified in the issue: PurchasedSectionName, PurchasedSectionRow,
     * PurchasedSeatRange, PurchasedStatus, PurchasedDate, PurchasedBy
     */
    const controls = [
        { component: 'text', name: 'siteId', label: 'Site ID', xs: 1.5, onChange: (params: any) => handleFilterChange(params) },
        { component: 'select', name: 'marketType', label: 'Type', xs: 1, choices: marketType || [], onChange: (params: any) => handleFilterChange(params) },
        { component: 'text', name: 'eventName', label: 'Event Name', xs: 2, onChange: (params: any) => handleFilterChange(params) },
        { component: 'text', name: 'venue', label: 'Venue', xs: 2, onChange: (params: any) => handleFilterChange(params) },
        { component: 'daterange', name: 'eventDate', label: 'Event Date', xs: 2, isUTC: true, onChange: (params: any) => handleDateRangeChange(params) },
        { component: 'daterange', name: 'lastSaleDate', label: 'Order Date', xs: 2, isUTC: true, onChange: (params: any) => handleDateRangeChange(params) },
        {
            component: 'select',
            name: 'purchaseStatus_eq',
            label: 'Status',
            xs: 1,
            choices: PURCHASE_STATUS_OPTIONS,
            onChange: (params: any) => handleFilterChange(params)
        },
        { component: 'text', name: 'purchasedSectionName', label: 'Section', xs: 2, onChange: (params: any) => handleFilterChange(params) },
        { component: 'text', name: 'purchasedRowName', label: 'Row', xs: 1, onChange: (params: any) => handleFilterChange(params) },
        { component: 'text', name: 'account', label: 'Purch. Account', xs: 1, onChange: (params: any) => handleFilterChange(params) },
        { component: 'daterange', name: 'purchaseDate', label: 'Purchase Date', xs: 2, isUTC: true, onChange: (params: any) => handleDateRangeChange(params) },
        { component: 'text', name: 'purchasedBy_UserId', label: 'Purchased By', xs: 1.5, onChange: (params: any) => handleFilterChange(params) },
        { component: 'select', name: 'trackingVersion', label: 'Tracking Version', xs: 1, choices: TrackingVersion || [], onChange: (params: any) => handleFilterChange(params) },
        { component: 'checkbox', name: 'highRisk_eq', label: 'High Risk', xs: 1, nullable: true, onChange: (params: any) => handleFilterChange(params) },
        { component: 'checkbox', name: 'removeIgnored', label: 'Ignored', xs: 1, nullable: true, onChange: (params: any) => handleFilterChange(params) },
    ];

    /**
     * Handles row click to open the seat action dialog
     * @param id - Record ID
     * @param resource - Resource name
     * @param record - The full purchase record
     */
    const handleRowClick = (id: string | number, resource: string, record: AIPurchasedTicket) => {
        const selection = window.getSelection()?.toString();
        if (selection && selection.length > 0) {
            // User is selecting text, do not open dialog
            return;
        }
        setSelectedPurchase(record);
        setSeatActionDialogOpen(true);
        return false; // Prevent default navigation
    };

    /**
     * Handles saving updated purchase data from the dialog
     * @param seatData - Updated purchase data
     */
    const handleSavePurchase = async (seatData: AIPurchasedTicket) => {
        try {
            // Update the purchased ticket record via dataProvider
            // Exclude computed fields from the update payload
            // const { totalPurchaseCost: _totalPurchaseCost, actualROI: _actualROI, ...updatePurchase } = seatData as AIPurchasedTicket;
            await dataProvider.postData('PurchasedTickets', 'UpdatePurchase', seatData, 'PUT')
                .then((response: any) => {
                    if (response && response.data === true) {
                        notify('Purchase updated successfully', { type: 'success' });
                    } else {
                        notify('Purchase updated failed', { type: 'error' });
                    }
                });
            
            
            setSeatActionDialogOpen(false);
            
            // Refresh the list
            if (refbtnFilter.current) {
                (refbtnFilter.current as HTMLButtonElement).click();
            }
        } catch (error: any) {
            notify(`Failed to update purchase: ${error.message}`, { type: 'error' });
        }
    };

    /**
     * Closes the seat action dialog
     */
    const handleCloseDialog = () => {
        setSeatActionDialogOpen(false);
        setSelectedPurchase(null);
    };

    /**
     * Handles the View button click to view detailed seat analysis results
     * @param record - The purchase record
     */
    const handleViewSeatAnalysis = (record: AIPurchasedTicket) => {
        try {
            // Validate that we have at least one ID to work with
            if (!((record.singleAnalysisId && record.singleAnalysisId > 0) ||
                  (record.seatAnalysisResultId && record.seatAnalysisResultId > 0))) {
                notify('No seat analysis result available for this purchase', { type: 'warning' });
                return;
            }
            notify('Loading analysis data...', { type: 'info' });
            // Set the selected event based on the purchased ticket - for display common event info immediately
            let selectEvent = EventItem.fromPurchasedTicket(record);
            setSelectedEvent(selectEvent);
            // Switch to Single Event Analysis tab to active fetch and display the analysis data
            setActiveTab(1);
        } catch (error: any) {
            console.error('Error viewing seat analysis:', error);
            notify(`Failed to load seat analysis: ${error.message || 'Unknown error'}`, { type: 'error' });
        }
    };

    return (
        <>
            <RaList
                storeKey={true}
                sortby={defaultSort}
                perPage={50}
                pagination={[50, 100, 200, 500, 1000]}
                title={'Purchase Seats'}
                resource="PurchasedTickets"
                sx={{ width: '99%', marginLeft: '8px' }}
                filters={
                    <RenderControls
                        
                        key={'purchase-seats-filters'}
                        defaultValues={defaultFilter}
                        controls={controls}
                        refFilter={refbtnFilter}
                        useHashUrl
                        exportFile={{
                            filename: `PurchaseSeats-${formatDateTime(toLocalTime(new Date()), 'YYYYY-MM-DD_HH-mm-ss')}`,
                            columns: [
                                'id',
                                'SiteId',
                                'EventName',
                                'Venue',
                                'EventDate',
                                'MarketType',
                                'PurchasedSectionName',
                                'PurchasedRowName',
                                'PurchasedSeatRange',
                                'PurchasedQuantity',
                                'ActualPricePerTicket',
                                'TotalPurchaseCost',
                                'PurchaseStatus',
                                'PurchaseDate',
                                'HighRisk',
                                'PurchasedBy_UserId',
                                'OriginalAIScore',
                            ],
                            limit: 1000000
                        }}
                    />
                }
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={handleRowClick}
                    sx={{
                        tableLayout: 'fixed',
                        '& .column-eventName': { minWidth: '110px' },
                        '& .column-venue': { minWidth: '100px' },
                        '& .column-purchasedSectionName': { minWidth: '140px' },
                    }}
                >
                    {/* <TextField source="id" label="ID" /> */}
                    <TextField source="siteId" label="Site ID" sx={{ maxWidth: '52px',  wordBreak: 'break-all' }}/>
                    // Add function field combined event name and market type
                    <FunctionField
                        label="Event Name"
                        source='eventName'
                        render={(record: AIPurchasedTicket) => (
                            <>
                                <span>{record.eventName || '-'}</span>
                                <br />
                                <a href={record.url} target="_blank" rel="noopener noreferrer" style={{ color: 'deepskyblue' }} onClick={(e) => e.stopPropagation()}>
                                    [{record.marketType || '-'}]
                                </a>
                            </>
                        )}
                    />
                    <TextField source="venue" label="Venue" />
                    <FunctionField
                        label="Event Date"
                        source="eventDate"
                        render={(record: AIPurchasedTicket) => {
                            let evDate = record.eventDate && record.eventDate !== '0001-01-01T00:00:00' ? record.eventDate : null;
                            return evDate ? (
                                <>
                                    <b>{record.eventDate ? formatDateTime(record.eventDate, 'MM/DD/YYYY') : '-'}</b>
                                    <br />
                                    {record.eventDate ? formatDateTime(record.eventDate, 'hh:mm A') : '-'}
                                </>
                            ) : '-';
                        }}
                    />
                    <FunctionField
                        label="Section / [Row]"
                        source='purchasedSectionName'
                        render={(record: AIPurchasedTicket) => (
                            <>
                                <span>{record.purchasedSectionName || '-'}</span>
                                <br />
                                <span style={{ color: 'orange' }}>[{record.purchasedRowName || '-'}]</span>
                            </>
                        )}
                    />
                    <TextField source="purchasedSeatRange" label="Seats" sx={{ maxWidth: '5px' }}/>
                    <TextField source="purchasedQuantity" label="Qty" sx={{ maxWidth: '5px' }}/>
                    <FunctionField
                        label="Price"
                        source='actualPricePerTicket'
                        render={(record: AIPurchasedTicket) =>
                            record.actualPricePerTicket ? `$${record.actualPricePerTicket.toFixed(2)}` : '-'
                        }
                    />
                    <FunctionField
                        label="Total Cost"
                        render={(record: AIPurchasedTicket) =>
                            record.totalPurchaseCost ? `$${record.totalPurchaseCost.toFixed(2)}` : '-'
                        }
                    />
                    <FunctionField
                        label="Total Profit / (ROI)"
                        source='actualROI'
                        render={(record: AIPurchasedTicket) => {
                            if (record.avgDaysToSell === null || record.avgDaysToSell === undefined || !record.lastSaleDate) return '-';
                            let profit = '-';
                            if (record.actualROI !== null && record.actualROI !== undefined) {
                                if (record.actualROI === 1 && (record.actualPricePerTicket === null || record.actualPricePerTicket === undefined || record.actualPricePerTicket === 0)) {
                                    profit = `$${record?.totalResaleRevenue?.toFixed(2)}`;
                                } else {
                                    const totalResaleRevenue = record.actualROI * (record.actualPricePerTicket ?? 0) * (record.purchasedQuantity ?? 0);
                                    profit = `$${totalResaleRevenue.toFixed(2)}`;
                                }
                            }
                            return (
                                <>
                                    <span style={{ color: record.actualROI! > 0 ? 'green' : 'red' }}>
                                        {profit}
                                    </span>
                                    <br />
                                    <span style={{ color: 'gray' }}>
                                        ({record.actualROI ? (record.actualROI * 100).toFixed(1) + '%' : '-'})
                                    </span>
                                </>
                            );
                        }}
                    />
                    <FunctionField
                        label="Avg Days to Sell"
                        source='avgDaysToSell'
                        render={(record: AIPurchasedTicket) =>
                            record.avgDaysToSell !== null && record.avgDaysToSell !== undefined && record.lastSaleDate ? record.avgDaysToSell.toFixed(2) : '-'
                        }
                    />
                    <FunctionField
                        label="Last Sale Date"
                        source="lastSaleDate"
                        render={(record: AIPurchasedTicket) => {
                            let evDate = record.lastSaleDate && record.lastSaleDate !== '0001-01-01T00:00:00' ? record.lastSaleDate : null;
                            return evDate ? (
                                <>
                                    <b>{record.lastSaleDate ? formatDateTime(record.lastSaleDate, 'MM/DD/YYYY') : '-'}</b>
                                    <br />
                                    {record.lastSaleDate ? formatDateTime(record.lastSaleDate, 'hh:mm A') : '-'}
                                </>
                            ) : '-';
                        }}
                    />
                    {/* // show icon warning if highRisk is true, else show nothing */}
                    <FunctionField
                        label="High Risk"
                        source='highRisk'
                        render={(record: AIPurchasedTicket) =>
                            record.highRisk ? (
                                <WarningAmber sx={{ color: 'red' }}/>
                            ) : '-'
                        }
                    />
                    <FunctionField
                        label="Status"
                        source='purchaseStatus'
                        render={(record: AIPurchasedTicket) => {
                            let pStatus = record.purchaseStatus || 'Unknown?';
                            if (pStatus === 'Active' && record.tags && record.tags.toLowerCase().includes('unbroadcast')) {
                                pStatus = 'New*';
                            }
                            return (
                                <>
                                    <Chip label={pStatus} color={getActionChipColor(pStatus)} size="small" />
                                </>
                            );}}
                    />
                    <FunctionField
                        label="Tracking Version"
                        source='trackingVersion'
                        render={(record: AIPurchasedTicket) =>
                            record.trackingVersion || '-'
                        }
                    />
                    <FunctionField
                        label="Purchase Date"
                        source="purchaseDate"
                        render={(record: AIPurchasedTicket) => (
                            <>
                                <b>{record.purchaseDate ? formatDateTime(toLocalTime(record.purchaseDate), 'MM/DD/YYYY') : '-'}</b>
                                <br />
                                {record.purchaseDate ? formatDateTime(toLocalTime(record.purchaseDate), 'hh:mm A') : '-'}
                            </>
                        )}
                    />
                    <EmailField source="account" label="Purchasing Account" sx={{ maxWidth: '50px',  wordBreak: 'break-all' }} />
                    <FunctionField
                        label="Seat Score"
                        source='originalAIScore'
                        render={(record: AIPurchasedTicket) =>
                            record.originalAIScore ? (
                                <Chip
                                    label={record.originalAIScore.toFixed(1)}
                                    color={getScoreColor(record.originalAIScore)}
                                    size="small"
                                />
                            ) : '-'
                        }
                    />
                    <FunctionField
                        label="View"
                        render={(record: AIPurchasedTicket) =>
                            record.singleAnalysisId && record.singleAnalysisId > 0 ? (
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewSeatAnalysis(record);
                                    }}
                                    aria-label="view seat analysis"
                                >
                                    <Visibility />
                                </IconButton>
                            ) : null
                        }
                    />
                </Datagrid>
            </RaList>

            {/* Seat Action Dialog for editing purchase details */}
            {selectedPurchase && (
                <SeatActionDialog
                    open={seatActionDialogOpen}
                    onClose={handleCloseDialog}
                    onSave={handleSavePurchase}
                    initialData={selectedPurchase}
                />
            )}
        </>
    );
};

export default PurchaseSeatsTab;
